// 261025 E3-P3f — nối dây «Duyệt & đẩy N tập»: xem thử (không ghi, có debounce) + bấm đẩy + huỷ + mở hộp kết quả. Quyết định/chữ ở `handoffBatchView.ts` (có jest); file này chỉ là hook React.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type { WorkbenchEpisode, WorkbenchHandoffBatchView } from '../../../types/adaptationWorkbench';
import { batchErrorView, batchItemsOf, batchOutcomeOf, cancelErrorText, PreviewState, shouldOpenResults } from './handoffBatchView';

export const PREVIEW_DEBOUNCE_MS = 300;

/**
 * Xem thử kế hoạch cho đúng danh sách tập đưa vào (`items` đã đủ băm). Gọi lại khi danh sách / băm / `dataVersion` đổi; KHÔNG gắn vào vòng poll.
 * `enabled=false` ⇒ `idle`, không gọi. Lỗi mạng/409 ⇒ `error` (nơi dùng vẫn cho đẩy — máy chủ kiểm lại).
 */
export type HandoffPreviewState = PreviewState & { retry: () => void };
export const useHandoffPreview = (storyId: string, items: Array<{ episode: number; contentSha: string }>, dataVersion: number, enabled: boolean): HandoffPreviewState => {
  const [state, setState] = useState<PreviewState>({ status: 'idle' });
  const [retryNonce, setRetryNonce] = useState(0);
  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);
  const key = useMemo(() => items.map((i) => `${i.episode}:${i.contentSha}`).join('|'), [items]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    if (!enabled || !key) { setState({ status: 'idle' }); return undefined; }
    let alive = true;
    setState((cur) => (cur.status === 'ok' ? cur : { status: 'loading' }));   // đang có số cũ thì giữ, khỏi nhấp nháy
    const t = setTimeout(() => {
      adaptationApi.handoffBatchPreview(storyId, itemsRef.current)
        .then((r) => { if (alive) setState({ status: 'ok', data: r }); })
        .catch(() => { if (alive) setState({ status: 'error' }); });
    }, PREVIEW_DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(t); };
  }, [storyId, key, dataVersion, enabled, retryNonce]);
  return { ...state, retry };
};

export interface UseHandoffBatchActions {
  /** Đang gửi POST (chưa có jobId). */
  starting: boolean;
  /** Hộp kết quả đang mở. */
  resultsOpen: boolean;
  openResults: () => void;
  closeResults: () => void;
  /** Gửi lô. `true` ⇒ máy chủ nhận (202) hoặc đã có lô đang chạy để bám theo. */
  start: (episodes: ReadonlyArray<Pick<WorkbenchEpisode, 'ep' | 'contentSha'>>, numbers: ReadonlyArray<number>) => Promise<boolean>;
  cancel: (jobId: string) => Promise<void>;
}

/**
 * Bấm đẩy / huỷ + tự mở hộp kết quả MỘT lần khi lô do phiên này bấm chạy xong (so `jobId` đang theo dõi với `workbench.handoffBatch`).
 * Tiến độ và kết quả đọc từ `workbench.handoffBatch` (poll 5s của workbench khi còn việc chạy) — hook không tự poll.
 */
export const useHandoffBatchActions = (
  storyId: string,
  batch: WorkbenchHandoffBatchView | null | undefined,
  refreshAll: () => Promise<unknown>,
): UseHandoffBatchActions => {
  const [starting, setStarting] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [watchJobId, setWatchJobId] = useState<string | null>(null);

  useEffect(() => {
    if (!shouldOpenResults(watchJobId, batch)) return;
    setWatchJobId(null);
    setResultsOpen(true);
    const o = batchOutcomeOf(batch);
    if (o) { if (o.level === 'warning') message.warning(o.text, 8); else message.success(o.text, 5); }
  }, [watchJobId, batch]);

  const start = useCallback<UseHandoffBatchActions['start']>(async (episodes, numbers) => {
    const items = batchItemsOf(episodes, numbers);
    if (items.length === 0) { message.warning('Chưa có tập nào đủ điều kiện để đẩy — đã tải lại', 5); await refreshAll(); return false; }
    setStarting(true);
    try {
      const r = await adaptationApi.handoffBatchStart(storyId, items);
      setWatchJobId(r.jobId);
      await refreshAll();
      return true;
    } catch (e) {
      const v = batchErrorView(e);
      message.warning(v.text, 8);
      if (v.jobId) setWatchJobId(v.jobId);
      if (v.reload) await refreshAll();
      return v.kind === 'running';
    } finally { setStarting(false); }
  }, [storyId, refreshAll]);

  const cancel = useCallback(async (jobId: string) => {
    try {
      await adaptationApi.handoffBatchCancel(jobId);
      message.info('Đã gửi lệnh dừng — tập đang đẩy dở sẽ xong rồi dừng, phần còn lại không đẩy', 6);
    } catch (e) { message.warning(cancelErrorText(e), 6); }
    await refreshAll();
  }, [refreshAll]);

  return { starting, resultsOpen, openResults: () => setResultsOpen(true), closeResults: () => setResultsOpen(false), start, cancel };
};
